import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Loader2,
  Search,
  Trash2,
} from "lucide-react";
import type { ArticleDate } from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

function formatDate(val: ArticleDate | undefined): string {
  if (!val) return "—";
  const str = typeof val === "string" ? val : val.$date;
  if (!str) return "—";
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ArticlesList() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const query = useQuery({
    queryKey: ["il-website", "articles", { page, search }],
    queryFn: () => api.ilWebsite.listArticles({ page, search }),
  });

  const deleteArticle = useMutation({
    mutationFn: (id: string) => api.ilWebsite.deleteArticle(id),
    onSuccess: (_, id) => {
      toast.success(`Deleted article ${id}`);
      void queryClient.invalidateQueries({
        queryKey: ["il-website", "articles"],
      });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to delete article"),
  });

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const articles = query.data?.articles ?? [];
  const totalPages = query.data?.totalPages ?? 1;

  return (
    <div dir="rtl" className="space-y-4">
      <Link
        to="/dashboard/il-website"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to IL Website
      </Link>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by ID or title..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="ps-10"
          />
        </div>
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {query.isError && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : "Failed to load articles"}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">
            Articles
            {!query.isLoading && articles.length > 0 && (
              <span className="ml-2 font-normal text-muted-foreground">
                (page {page}, 10 per page)
              </span>
            )}
          </h2>
        </div>

        {query.isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : articles.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {search ? "No articles match your search." : "No articles found."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="w-40">Author</TableHead>
                <TableHead className="w-40">Date</TableHead>
                <TableHead className="w-28 text-end">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.map((a) => {
                const id = String(a._id);
                return (
                  <TableRow key={id}>
                    <TableCell className="font-mono text-sm font-semibold">
                      {id}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="truncate text-sm font-medium" dir="rtl">
                        {a.title || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                      {a.description && (
                        <p
                          className="mt-1 truncate text-xs text-muted-foreground"
                          dir="rtl"
                        >
                          {a.description.slice(0, 100)}…
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.authorTranslatedName ?? a.author ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(a.date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          asChild
                          variant="ghost"
                          size="icon"
                          title="Edit"
                        >
                          <Link
                            to={`/dashboard/il-website/articles-editor/${encodeURIComponent(id)}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Delete"
                          disabled={deleteArticle.isPending}
                          className="hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            if (confirm(`Delete article ${id}?`)) {
                              deleteArticle.mutate(id);
                            }
                          }}
                        >
                          {deleteArticle.isPending &&
                          deleteArticle.variables === id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {(totalPages > 1 || page > 1) && !query.isLoading && (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <p className="text-sm text-muted-foreground">Page {page}</p>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={articles.length < 10}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
